import type { CatalogApi } from "../../api/CatalogApi";
import type { AgreementCategoryFetchOptions } from "../../generated/dto/AgreementCategoryFetchOptions";
import type { AgreementCategoryStaff } from "../../generated/dto/AgreementCategoryStaff";
import type { AgreementFetchOptions } from "../../generated/dto/AgreementFetchOptions";
import type { AgreementInstance } from "../../generated/dto/AgreementInstance";
import type { AgreementInstanceFetchOptions } from "../../generated/dto/AgreementInstanceFetchOptions";
import type { AgreementInstanceSearchParams } from "../../generated/dto/AgreementInstanceSearchParams";
import type { AgreementInstanceStaff } from "../../generated/dto/AgreementInstanceStaff";
import type { AgreementInstanceStaffSearchResult } from "../../generated/dto/AgreementInstanceStaffSearchResult";
import type { AgreementRef } from "../../generated/dto/AgreementRef";
import type { AgreementSearchParams } from "../../generated/dto/AgreementSearchParams";
import type { AgreementStaff } from "../../generated/dto/AgreementStaff";
import type { AgreementStaffSearchResult } from "../../generated/dto/AgreementStaffSearchResult";
import type { AgreementVersionChangeRequest } from "../../generated/dto/AgreementVersionChangeRequest";
import type { CatalogDiscountFetchOptions } from "../../generated/dto/CatalogDiscountFetchOptions";
import type { CatalogDiscountRef } from "../../generated/dto/CatalogDiscountRef";
import type { CatalogDiscountStaff } from "../../generated/dto/CatalogDiscountStaff";
import type { CatalogFetchOptions } from "../../generated/dto/CatalogFetchOptions";
import type { CatalogItemAgreementFetchOptions } from "../../generated/dto/CatalogItemAgreementFetchOptions";
import type { CatalogItemAgreementStaff } from "../../generated/dto/CatalogItemAgreementStaff";
import type { CatalogItemAgreementsStaffAssignmentInput } from "../../generated/dto/CatalogItemAgreementsStaffAssignmentInput";
import type { CatalogItemCategoriesStaffAssignmentInput } from "../../generated/dto/CatalogItemCategoriesStaffAssignmentInput";
import type { CatalogItemCategoryFetchOptions } from "../../generated/dto/CatalogItemCategoryFetchOptions";
import type { CatalogItemCategoryStaff } from "../../generated/dto/CatalogItemCategoryStaff";
import type { CatalogItemDiscountFetchOptions } from "../../generated/dto/CatalogItemDiscountFetchOptions";
import type { CatalogItemDiscountStaff } from "../../generated/dto/CatalogItemDiscountStaff";
import type { CatalogItemFetchOptions } from "../../generated/dto/CatalogItemFetchOptions";
import type { CatalogItemInstance } from "../../generated/dto/CatalogItemInstance";
import type { CatalogItemInstanceFetchOptions } from "../../generated/dto/CatalogItemInstanceFetchOptions";
import type { CatalogItemInstanceMember } from "../../generated/dto/CatalogItemInstanceMember";
import type { CatalogItemInstanceStaff } from "../../generated/dto/CatalogItemInstanceStaff";
import type { CatalogItemItemDiscountsStaffAssignmentInput } from "../../generated/dto/CatalogItemItemDiscountsStaffAssignmentInput";
import type { CatalogItemRef } from "../../generated/dto/CatalogItemRef";
import type { CatalogItemSearchParams } from "../../generated/dto/CatalogItemSearchParams";
import type { CatalogItemStaff } from "../../generated/dto/CatalogItemStaff";
import type { CatalogItemStaffSearchResult } from "../../generated/dto/CatalogItemStaffSearchResult";
import type { CatalogItemTaxFetchOptions } from "../../generated/dto/CatalogItemTaxFetchOptions";
import type { CatalogItemTaxStaff } from "../../generated/dto/CatalogItemTaxStaff";
import type { CatalogItemTaxesStaffAssignmentInput } from "../../generated/dto/CatalogItemTaxesStaffAssignmentInput";
import type { CatalogPriceChangeRequest } from "../../generated/dto/CatalogPriceChangeRequest";
import type { CatalogSearchParams } from "../../generated/dto/CatalogSearchParams";
import type { CatalogStaff } from "../../generated/dto/CatalogStaff";
import type { CatalogStaffSearchResult } from "../../generated/dto/CatalogStaffSearchResult";
import type { CatalogTaxFetchOptions } from "../../generated/dto/CatalogTaxFetchOptions";
import type { CatalogTaxRef } from "../../generated/dto/CatalogTaxRef";
import type { CatalogTaxStaff } from "../../generated/dto/CatalogTaxStaff";
import type { CategoryFetchOptions } from "../../generated/dto/CategoryFetchOptions";
import type { CategoryMember } from "../../generated/dto/CategoryMember";
import type { CategoryMemberFetchOptions } from "../../generated/dto/CategoryMemberFetchOptions";
import type { CategoryRef } from "../../generated/dto/CategoryRef";
import type { CategorySearchParams } from "../../generated/dto/CategorySearchParams";
import type { CategoryStaff } from "../../generated/dto/CategoryStaff";
import type { CategoryStaffSearchResult } from "../../generated/dto/CategoryStaffSearchResult";
import type { CreateAgreement } from "../../generated/dto/CreateAgreement";
import type { CreateAgreementCategory } from "../../generated/dto/CreateAgreementCategory";
import type { CreateAgreementInstance } from "../../generated/dto/CreateAgreementInstance";
import type { CreateCatalog } from "../../generated/dto/CreateCatalog";
import type { CreateCatalogItemAgreement } from "../../generated/dto/CreateCatalogItemAgreement";
import type { CreateCatalogItemCategory } from "../../generated/dto/CreateCatalogItemCategory";
import type { CreateCatalogItemInstance } from "../../generated/dto/CreateCatalogItemInstance";
import type { CreateCatalogItemStaff } from "../../generated/dto/CreateCatalogItemStaff";
import type { CreateCategory } from "../../generated/dto/CreateCategory";
import type { CreateProduct } from "../../generated/dto/CreateProduct";
import type { DeleteAgreementCategoryStaff } from "../../generated/dto/DeleteAgreementCategoryStaff";
import type { DeleteAgreementInstanceStaff } from "../../generated/dto/DeleteAgreementInstanceStaff";
import type { DeleteAgreementStaff } from "../../generated/dto/DeleteAgreementStaff";
import type { DeleteCatalogItemAgreementStaff } from "../../generated/dto/DeleteCatalogItemAgreementStaff";
import type { DeleteCatalogItemCategoryStaff } from "../../generated/dto/DeleteCatalogItemCategoryStaff";
import type { DeleteCatalogItemInstanceStaff } from "../../generated/dto/DeleteCatalogItemInstanceStaff";
import type { DeleteCatalogItemStaff } from "../../generated/dto/DeleteCatalogItemStaff";
import type { DeleteCatalogStaff } from "../../generated/dto/DeleteCatalogStaff";
import type { DeleteCategoryStaff } from "../../generated/dto/DeleteCategoryStaff";
import type { DeleteProductStaff } from "../../generated/dto/DeleteProductStaff";
import type { ProductFetchOptions } from "../../generated/dto/ProductFetchOptions";
import type { ProductSearchParams } from "../../generated/dto/ProductSearchParams";
import type { ProductStaff } from "../../generated/dto/ProductStaff";
import type { ProductStaffSearchResult } from "../../generated/dto/ProductStaffSearchResult";
import type { SaveCatalogItemRequest } from "../../generated/dto/SaveCatalogItemRequest";
import type { StockChangeFetchOptions } from "../../generated/dto/StockChangeFetchOptions";
import type { StockChangeStaff } from "../../generated/dto/StockChangeStaff";
import type { UpdateAgreement } from "../../generated/dto/UpdateAgreement";
import type { UpdateAgreementCategory } from "../../generated/dto/UpdateAgreementCategory";
import type { UpdateAgreementInstance } from "../../generated/dto/UpdateAgreementInstance";
import type { UpdateCatalog } from "../../generated/dto/UpdateCatalog";
import type { UpdateCatalogItemAgreement } from "../../generated/dto/UpdateCatalogItemAgreement";
import type { UpdateCatalogItemCategory } from "../../generated/dto/UpdateCatalogItemCategory";
import type { UpdateCatalogItemInstance } from "../../generated/dto/UpdateCatalogItemInstance";
import type { UpdateCatalogItemStaff } from "../../generated/dto/UpdateCatalogItemStaff";
import type { UpdateCategory } from "../../generated/dto/UpdateCategory";
import type { UpdateProduct } from "../../generated/dto/UpdateProduct";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class CatalogFrontendService {
  constructor(private readonly api: CatalogApi) {}
  private readonly categoryMemberCache: Map<number, CategoryMember> = new Map();
  private readonly categoryStaffCache: Map<number, CategoryStaff> = new Map();
  private readonly agreementStaffCache: Map<number, AgreementStaff> = new Map();
  private readonly agreementInstanceStaffCache: Map<number, AgreementInstanceStaff> = new Map();
  private readonly agreementCategoryStaffCache: Map<number, AgreementCategoryStaff> = new Map();
  private readonly catalogStaffCache: Map<number, CatalogStaff> = new Map();
  private readonly productStaffCache: Map<number, ProductStaff> = new Map();
  private readonly catalogItemStaffCache: Map<number, CatalogItemStaff> = new Map();
  private readonly catalogItemInstanceStaffCache: Map<number, CatalogItemInstanceStaff> = new Map();
  async getCategoryMember(id: number, options?: CategoryMemberFetchOptions): Promise<CategoryMember> {
    const __cached = this.categoryMemberCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getCategoryMember(id, options);
    this.categoryMemberCache.set(id, __value);
    return __value
  }
  async getCategoryStaff(id: number, options?: CategoryFetchOptions): Promise<CategoryStaff> {
    const __cached = this.categoryStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getCategoryStaff(id, options);
    this.categoryStaffCache.set(id, __value);
    return __value
  }
  async getAgreementStaff(id: number, options?: AgreementFetchOptions): Promise<AgreementStaff> {
    const __cached = this.agreementStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getAgreementStaff(id, options);
    this.agreementStaffCache.set(id, __value);
    return __value
  }
  async getAgreementInstanceStaff(id: number, options?: AgreementInstanceFetchOptions): Promise<AgreementInstanceStaff> {
    const __cached = this.agreementInstanceStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getAgreementInstanceStaff(id, options);
    this.agreementInstanceStaffCache.set(id, __value);
    return __value
  }
  async getAgreementCategoryStaff(id: number, options?: AgreementCategoryFetchOptions): Promise<AgreementCategoryStaff> {
    const __cached = this.agreementCategoryStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getAgreementCategoryStaff(id, options);
    this.agreementCategoryStaffCache.set(id, __value);
    return __value
  }
  async getCatalogStaff(id: number, options?: CatalogFetchOptions): Promise<CatalogStaff> {
    const __cached = this.catalogStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getCatalogStaff(id, options);
    this.catalogStaffCache.set(id, __value);
    return __value
  }
  async getProductStaff(id: number, options?: ProductFetchOptions): Promise<ProductStaff> {
    const __cached = this.productStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getProductStaff(id, options);
    this.productStaffCache.set(id, __value);
    return __value
  }
  async getCatalogItemStaff(id: number, options?: CatalogItemFetchOptions): Promise<CatalogItemStaff> {
    const __cached = this.catalogItemStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getCatalogItemStaff(id, options);
    this.catalogItemStaffCache.set(id, __value);
    return __value
  }
  async getCatalogItemInstanceStaff(id: number, options?: CatalogItemInstanceFetchOptions): Promise<CatalogItemInstanceStaff> {
    const __cached = this.catalogItemInstanceStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getCatalogItemInstanceStaff(id, options);
    this.catalogItemInstanceStaffCache.set(id, __value);
    return __value
  }
  async getCatalogItemCategoryStaff(id: number, options?: CatalogItemCategoryFetchOptions): Promise<CatalogItemCategoryStaff> {
    return this.api.getCatalogItemCategoryStaff(id, options)
  }
  async getCatalogItemAgreementStaff(id: number, options?: CatalogItemAgreementFetchOptions): Promise<CatalogItemAgreementStaff> {
    return this.api.getCatalogItemAgreementStaff(id, options)
  }
  async getAllCategoryMembers(options?: CategoryMemberFetchOptions): Promise<Array<CategoryMember>> {
    return this.api.getAllCategoryMembers(options)
  }
  async getAllCategoryStaffs(options?: CategoryFetchOptions): Promise<Array<CategoryStaff>> {
    return this.api.getAllCategoryStaffs(options)
  }
  async getAllAgreementStaffs(options?: AgreementFetchOptions): Promise<Array<AgreementStaff>> {
    return this.api.getAllAgreementStaffs(options)
  }
  async getAllAgreementInstanceStaffs(options?: AgreementInstanceFetchOptions): Promise<Array<AgreementInstanceStaff>> {
    return this.api.getAllAgreementInstanceStaffs(options)
  }
  async getAllAgreementCategoryStaffs(options?: AgreementCategoryFetchOptions): Promise<Array<AgreementCategoryStaff>> {
    return this.api.getAllAgreementCategoryStaffs(options)
  }
  async getAllCatalogStaffs(options?: CatalogFetchOptions): Promise<Array<CatalogStaff>> {
    return this.api.getAllCatalogStaffs(options)
  }
  async getAllProductStaffs(options?: ProductFetchOptions): Promise<Array<ProductStaff>> {
    return this.api.getAllProductStaffs(options)
  }
  async getAllCatalogItemStaffs(options?: CatalogItemFetchOptions): Promise<Array<CatalogItemStaff>> {
    return this.api.getAllCatalogItemStaffs(options)
  }
  async getAllCatalogItemInstanceStaffs(options?: CatalogItemInstanceFetchOptions): Promise<Array<CatalogItemInstanceStaff>> {
    return this.api.getAllCatalogItemInstanceStaffs(options)
  }
  async createCategoryStaff(source: CreateCategory): Promise<CategoryStaff> {
    const __result = await this.api.createCategoryStaff(source);
    this.categoryMemberCache.clear();
    this.categoryStaffCache.clear();
    return __result
  }
  async createAgreementStaff(source: CreateAgreement): Promise<AgreementStaff> {
    const __result = await this.api.createAgreementStaff(source);
    this.agreementStaffCache.clear();
    return __result
  }
  async createAgreementInstanceStaff(source: CreateAgreementInstance): Promise<AgreementInstanceStaff> {
    const __result = await this.api.createAgreementInstanceStaff(source);
    this.agreementInstanceStaffCache.clear();
    return __result
  }
  async createAgreementCategoryStaff(source: CreateAgreementCategory): Promise<AgreementCategoryStaff> {
    const __result = await this.api.createAgreementCategoryStaff(source);
    this.agreementCategoryStaffCache.clear();
    return __result
  }
  async createCatalogStaff(source: CreateCatalog): Promise<CatalogStaff> {
    const __result = await this.api.createCatalogStaff(source);
    this.catalogStaffCache.clear();
    return __result
  }
  async createProductStaff(source: CreateProduct): Promise<ProductStaff> {
    const __result = await this.api.createProductStaff(source);
    this.productStaffCache.clear();
    return __result
  }
  async createCatalogItemStaff(source: CreateCatalogItemStaff): Promise<CatalogItemStaff> {
    const __result = await this.api.createCatalogItemStaff(source);
    this.catalogItemStaffCache.clear();
    return __result
  }
  async createCatalogItemInstanceStaff(source: CreateCatalogItemInstance): Promise<CatalogItemInstanceStaff> {
    const __result = await this.api.createCatalogItemInstanceStaff(source);
    this.catalogItemInstanceStaffCache.clear();
    return __result
  }
  async createCatalogItemCategoryStaff(source: CreateCatalogItemCategory): Promise<CatalogItemCategoryStaff> {
    return this.api.createCatalogItemCategoryStaff(source)
  }
  async createCatalogItemAgreementStaff(source: CreateCatalogItemAgreement): Promise<CatalogItemAgreementStaff> {
    return this.api.createCatalogItemAgreementStaff(source)
  }
  async updateCategoryStaff(source: UpdateCategory): Promise<CategoryStaff> {
    const __result = await this.api.updateCategoryStaff(source);
    this.categoryMemberCache.clear();
    this.categoryStaffCache.clear();
    return __result
  }
  async updateAgreementStaff(source: UpdateAgreement): Promise<AgreementStaff> {
    const __result = await this.api.updateAgreementStaff(source);
    this.agreementStaffCache.clear();
    return __result
  }
  async updateAgreementInstanceStaff(source: UpdateAgreementInstance): Promise<AgreementInstanceStaff> {
    const __result = await this.api.updateAgreementInstanceStaff(source);
    this.agreementInstanceStaffCache.clear();
    return __result
  }
  async updateAgreementCategoryStaff(source: UpdateAgreementCategory): Promise<AgreementCategoryStaff> {
    const __result = await this.api.updateAgreementCategoryStaff(source);
    this.agreementCategoryStaffCache.clear();
    return __result
  }
  async updateCatalogStaff(source: UpdateCatalog): Promise<CatalogStaff> {
    const __result = await this.api.updateCatalogStaff(source);
    this.catalogStaffCache.clear();
    return __result
  }
  async updateProductStaff(source: UpdateProduct): Promise<ProductStaff> {
    const __result = await this.api.updateProductStaff(source);
    this.productStaffCache.clear();
    return __result
  }
  async updateCatalogItemStaff(source: UpdateCatalogItemStaff): Promise<CatalogItemStaff> {
    const __result = await this.api.updateCatalogItemStaff(source);
    this.catalogItemStaffCache.clear();
    return __result
  }
  async updateCatalogItemInstanceStaff(source: UpdateCatalogItemInstance): Promise<CatalogItemInstanceStaff> {
    const __result = await this.api.updateCatalogItemInstanceStaff(source);
    this.catalogItemInstanceStaffCache.clear();
    return __result
  }
  async updateCatalogItemCategoryStaff(source: UpdateCatalogItemCategory): Promise<CatalogItemCategoryStaff> {
    return this.api.updateCatalogItemCategoryStaff(source)
  }
  async updateCatalogItemAgreementStaff(source: UpdateCatalogItemAgreement): Promise<CatalogItemAgreementStaff> {
    return this.api.updateCatalogItemAgreementStaff(source)
  }
  async deleteCategoryStaff(source: DeleteCategoryStaff): Promise<void> {
    const __result = await this.api.deleteCategoryStaff(source);
    this.categoryMemberCache.clear();
    this.categoryStaffCache.clear();
    return __result
  }
  async deleteAgreementStaff(source: DeleteAgreementStaff): Promise<void> {
    const __result = await this.api.deleteAgreementStaff(source);
    this.agreementStaffCache.clear();
    return __result
  }
  async deleteAgreementInstanceStaff(source: DeleteAgreementInstanceStaff): Promise<void> {
    const __result = await this.api.deleteAgreementInstanceStaff(source);
    this.agreementInstanceStaffCache.clear();
    return __result
  }
  async deleteAgreementCategoryStaff(source: DeleteAgreementCategoryStaff): Promise<void> {
    const __result = await this.api.deleteAgreementCategoryStaff(source);
    this.agreementCategoryStaffCache.clear();
    return __result
  }
  async deleteCatalogStaff(source: DeleteCatalogStaff): Promise<void> {
    const __result = await this.api.deleteCatalogStaff(source);
    this.catalogStaffCache.clear();
    return __result
  }
  async deleteProductStaff(source: DeleteProductStaff): Promise<void> {
    const __result = await this.api.deleteProductStaff(source);
    this.productStaffCache.clear();
    return __result
  }
  async deleteCatalogItemStaff(source: DeleteCatalogItemStaff): Promise<void> {
    const __result = await this.api.deleteCatalogItemStaff(source);
    this.catalogItemStaffCache.clear();
    return __result
  }
  async deleteCatalogItemInstanceStaff(source: DeleteCatalogItemInstanceStaff): Promise<void> {
    const __result = await this.api.deleteCatalogItemInstanceStaff(source);
    this.catalogItemInstanceStaffCache.clear();
    return __result
  }
  async deleteCatalogItemCategoryStaff(source: DeleteCatalogItemCategoryStaff): Promise<void> {
    return this.api.deleteCatalogItemCategoryStaff(source)
  }
  async deleteCatalogItemAgreementStaff(source: DeleteCatalogItemAgreementStaff): Promise<void> {
    return this.api.deleteCatalogItemAgreementStaff(source)
  }
  async searchCategoryStaff(params: CategorySearchParams, page: number, perPage: number): Promise<PaginationResult<CategoryStaffSearchResult>> {
    return this.api.searchCategoryStaff(params, page, perPage)
  }
  async searchAgreementStaff(params: AgreementSearchParams, page: number, perPage: number): Promise<PaginationResult<AgreementStaffSearchResult>> {
    return this.api.searchAgreementStaff(params, page, perPage)
  }
  async searchAgreementInstanceStaff(params: AgreementInstanceSearchParams, page: number, perPage: number): Promise<PaginationResult<AgreementInstanceStaffSearchResult>> {
    return this.api.searchAgreementInstanceStaff(params, page, perPage)
  }
  async searchCatalogStaff(params: CatalogSearchParams, page: number, perPage: number): Promise<PaginationResult<CatalogStaffSearchResult>> {
    return this.api.searchCatalogStaff(params, page, perPage)
  }
  async searchProductStaff(params: ProductSearchParams, page: number, perPage: number): Promise<PaginationResult<ProductStaffSearchResult>> {
    return this.api.searchProductStaff(params, page, perPage)
  }
  async searchCatalogItemStaff(params: CatalogItemSearchParams, page: number, perPage: number): Promise<PaginationResult<CatalogItemStaffSearchResult>> {
    return this.api.searchCatalogItemStaff(params, page, perPage)
  }
  async searchCategoryStaffFull(params: CategorySearchParams, page: number, perPage: number, options?: CategoryFetchOptions): Promise<PaginationResult<CategoryStaff>> {
    return this.api.searchCategoryStaffFull(params, page, perPage, options)
  }
  async searchAgreementStaffFull(params: AgreementSearchParams, page: number, perPage: number, options?: AgreementFetchOptions): Promise<PaginationResult<AgreementStaff>> {
    return this.api.searchAgreementStaffFull(params, page, perPage, options)
  }
  async searchAgreementInstanceStaffFull(params: AgreementInstanceSearchParams, page: number, perPage: number, options?: AgreementInstanceFetchOptions): Promise<PaginationResult<AgreementInstanceStaff>> {
    return this.api.searchAgreementInstanceStaffFull(params, page, perPage, options)
  }
  async searchCatalogStaffFull(params: CatalogSearchParams, page: number, perPage: number, options?: CatalogFetchOptions): Promise<PaginationResult<CatalogStaff>> {
    return this.api.searchCatalogStaffFull(params, page, perPage, options)
  }
  async searchProductStaffFull(params: ProductSearchParams, page: number, perPage: number, options?: ProductFetchOptions): Promise<PaginationResult<ProductStaff>> {
    return this.api.searchProductStaffFull(params, page, perPage, options)
  }
  async searchCatalogItemStaffFull(params: CatalogItemSearchParams, page: number, perPage: number, options?: CatalogItemFetchOptions): Promise<PaginationResult<CatalogItemStaff>> {
    return this.api.searchCatalogItemStaffFull(params, page, perPage, options)
  }
  async getCategoryStaffItemCategories(id: number, options?: CatalogItemCategoryFetchOptions): Promise<Array<CatalogItemCategoryStaff>> {
    return this.api.getCategoryStaffItemCategories(id, options)
  }
  async getCategoryStaffAgreementCategories(id: number, options?: AgreementCategoryFetchOptions): Promise<Array<AgreementCategoryStaff>> {
    return this.api.getCategoryStaffAgreementCategories(id, options)
  }
  async getAgreementStaffCatalogId(id: number, options?: CatalogFetchOptions): Promise<CatalogStaff> {
    return this.api.getAgreementStaffCatalogId(id, options)
  }
  async getAgreementStaffItems(id: number, options?: CatalogItemAgreementFetchOptions): Promise<Array<CatalogItemAgreementStaff>> {
    return this.api.getAgreementStaffItems(id, options)
  }
  async getAgreementStaffAgreementInstances(id: number, options?: AgreementInstanceFetchOptions): Promise<Array<AgreementInstanceStaff>> {
    return this.api.getAgreementStaffAgreementInstances(id, options)
  }
  async getAgreementStaffAgreementCategories(id: number, options?: AgreementCategoryFetchOptions): Promise<Array<AgreementCategoryStaff>> {
    return this.api.getAgreementStaffAgreementCategories(id, options)
  }
  async getAgreementInstanceStaffAgreementId(id: number, options?: AgreementFetchOptions): Promise<AgreementStaff> {
    return this.api.getAgreementInstanceStaffAgreementId(id, options)
  }
  async getAgreementCategoryStaffAgreementId(id: number, options?: AgreementFetchOptions): Promise<AgreementStaff | null> {
    return this.api.getAgreementCategoryStaffAgreementId(id, options)
  }
  async getAgreementCategoryStaffCategoryId(id: number, options?: CategoryFetchOptions): Promise<CategoryStaff> {
    return this.api.getAgreementCategoryStaffCategoryId(id, options)
  }
  async getCatalogStaffItems(id: number, options?: CatalogItemFetchOptions): Promise<Array<CatalogItemStaff>> {
    return this.api.getCatalogStaffItems(id, options)
  }
  async getCatalogStaffProducts(id: number, options?: ProductFetchOptions): Promise<Array<ProductStaff>> {
    return this.api.getCatalogStaffProducts(id, options)
  }
  async getCatalogStaffAgreements(id: number, options?: AgreementFetchOptions): Promise<Array<AgreementStaff>> {
    return this.api.getCatalogStaffAgreements(id, options)
  }
  async getCatalogStaffTaxes(id: number, options?: CatalogTaxFetchOptions): Promise<Array<CatalogTaxStaff>> {
    return this.api.getCatalogStaffTaxes(id, options)
  }
  async getCatalogStaffDiscounts(id: number, options?: CatalogDiscountFetchOptions): Promise<Array<CatalogDiscountStaff>> {
    return this.api.getCatalogStaffDiscounts(id, options)
  }
  async getProductStaffCatalogId(id: number, options?: CatalogFetchOptions): Promise<CatalogStaff> {
    return this.api.getProductStaffCatalogId(id, options)
  }
  async getProductStaffItems(id: number, options?: CatalogItemFetchOptions): Promise<Array<CatalogItemStaff>> {
    return this.api.getProductStaffItems(id, options)
  }
  async getCatalogItemStaffCatalogId(id: number, options?: CatalogFetchOptions): Promise<CatalogStaff | null> {
    return this.api.getCatalogItemStaffCatalogId(id, options)
  }
  async getCatalogItemStaffItemInstances(id: number, options?: CatalogItemInstanceFetchOptions): Promise<Array<CatalogItemInstanceStaff>> {
    return this.api.getCatalogItemStaffItemInstances(id, options)
  }
  async getCatalogItemStaffCategories(id: number, options?: CatalogItemCategoryFetchOptions): Promise<Array<CatalogItemCategoryStaff>> {
    return this.api.getCatalogItemStaffCategories(id, options)
  }
  async getCatalogItemStaffAgreements(id: number, options?: CatalogItemAgreementFetchOptions): Promise<Array<CatalogItemAgreementStaff>> {
    return this.api.getCatalogItemStaffAgreements(id, options)
  }
  async getCatalogItemStaffTaxes(id: number, options?: CatalogItemTaxFetchOptions): Promise<Array<CatalogItemTaxStaff>> {
    return this.api.getCatalogItemStaffTaxes(id, options)
  }
  async getCatalogItemStaffItemDiscounts(id: number, options?: CatalogItemDiscountFetchOptions): Promise<Array<CatalogItemDiscountStaff>> {
    return this.api.getCatalogItemStaffItemDiscounts(id, options)
  }
  async getCatalogItemStaffProductId(id: number, options?: ProductFetchOptions): Promise<ProductStaff | null> {
    return this.api.getCatalogItemStaffProductId(id, options)
  }
  async getCatalogItemInstanceStaffItemId(id: number, options?: CatalogItemFetchOptions): Promise<CatalogItemStaff> {
    return this.api.getCatalogItemInstanceStaffItemId(id, options)
  }
  async getCatalogItemInstanceStaffStockChanges(id: number, options?: StockChangeFetchOptions): Promise<Array<StockChangeStaff>> {
    return this.api.getCatalogItemInstanceStaffStockChanges(id, options)
  }
  async getCatalogItemCategoryStaffItem(id: number, options?: CatalogItemFetchOptions): Promise<CatalogItemStaff> {
    return this.api.getCatalogItemCategoryStaffItem(id, options)
  }
  async getCatalogItemCategoryStaffCategoryId(id: number, options?: CategoryFetchOptions): Promise<CategoryStaff> {
    return this.api.getCatalogItemCategoryStaffCategoryId(id, options)
  }
  async getCatalogItemAgreementStaffItemId(id: number, options?: CatalogItemFetchOptions): Promise<CatalogItemStaff> {
    return this.api.getCatalogItemAgreementStaffItemId(id, options)
  }
  async getCatalogItemAgreementStaffAgreementId(id: number, options?: AgreementFetchOptions): Promise<AgreementStaff> {
    return this.api.getCatalogItemAgreementStaffAgreementId(id, options)
  }
  async addCatalogItemStaffCategories(parent: CatalogItemRef, values: Array<CatalogItemCategoriesStaffAssignmentInput>): Promise<Array<CatalogItemCategoryStaff>> {
    return this.api.addCatalogItemStaffCategories(parent, values)
  }
  async putCatalogItemStaffCategories(parent: CatalogItemRef, values: Array<CatalogItemCategoriesStaffAssignmentInput>): Promise<Array<CatalogItemCategoryStaff>> {
    return this.api.putCatalogItemStaffCategories(parent, values)
  }
  async removeCatalogItemStaffCategories(parent: CatalogItemRef, values: Array<CategoryRef>): Promise<Array<CatalogItemCategoryStaff>> {
    return this.api.removeCatalogItemStaffCategories(parent, values)
  }
  async clearCatalogItemStaffCategories(parent: CatalogItemRef): Promise<Array<CatalogItemCategoryStaff>> {
    return this.api.clearCatalogItemStaffCategories(parent)
  }
  async addCatalogItemStaffAgreements(parent: CatalogItemRef, values: Array<CatalogItemAgreementsStaffAssignmentInput>): Promise<Array<CatalogItemAgreementStaff>> {
    return this.api.addCatalogItemStaffAgreements(parent, values)
  }
  async putCatalogItemStaffAgreements(parent: CatalogItemRef, values: Array<CatalogItemAgreementsStaffAssignmentInput>): Promise<Array<CatalogItemAgreementStaff>> {
    return this.api.putCatalogItemStaffAgreements(parent, values)
  }
  async removeCatalogItemStaffAgreements(parent: CatalogItemRef, values: Array<AgreementRef>): Promise<Array<CatalogItemAgreementStaff>> {
    return this.api.removeCatalogItemStaffAgreements(parent, values)
  }
  async clearCatalogItemStaffAgreements(parent: CatalogItemRef): Promise<Array<CatalogItemAgreementStaff>> {
    return this.api.clearCatalogItemStaffAgreements(parent)
  }
  async addCatalogItemStaffTaxes(parent: CatalogItemRef, values: Array<CatalogItemTaxesStaffAssignmentInput>): Promise<Array<CatalogItemTaxStaff>> {
    return this.api.addCatalogItemStaffTaxes(parent, values)
  }
  async putCatalogItemStaffTaxes(parent: CatalogItemRef, values: Array<CatalogItemTaxesStaffAssignmentInput>): Promise<Array<CatalogItemTaxStaff>> {
    return this.api.putCatalogItemStaffTaxes(parent, values)
  }
  async removeCatalogItemStaffTaxes(parent: CatalogItemRef, values: Array<CatalogTaxRef>): Promise<Array<CatalogItemTaxStaff>> {
    return this.api.removeCatalogItemStaffTaxes(parent, values)
  }
  async clearCatalogItemStaffTaxes(parent: CatalogItemRef): Promise<Array<CatalogItemTaxStaff>> {
    return this.api.clearCatalogItemStaffTaxes(parent)
  }
  async addCatalogItemStaffItemDiscounts(parent: CatalogItemRef, values: Array<CatalogItemItemDiscountsStaffAssignmentInput>): Promise<Array<CatalogItemDiscountStaff>> {
    return this.api.addCatalogItemStaffItemDiscounts(parent, values)
  }
  async putCatalogItemStaffItemDiscounts(parent: CatalogItemRef, values: Array<CatalogItemItemDiscountsStaffAssignmentInput>): Promise<Array<CatalogItemDiscountStaff>> {
    return this.api.putCatalogItemStaffItemDiscounts(parent, values)
  }
  async removeCatalogItemStaffItemDiscounts(parent: CatalogItemRef, values: Array<CatalogDiscountRef>): Promise<Array<CatalogItemDiscountStaff>> {
    return this.api.removeCatalogItemStaffItemDiscounts(parent, values)
  }
  async clearCatalogItemStaffItemDiscounts(parent: CatalogItemRef): Promise<Array<CatalogItemDiscountStaff>> {
    return this.api.clearCatalogItemStaffItemDiscounts(parent)
  }
  async browseItemsPublic(): Promise<Array<CatalogItemInstanceMember>> {
    return this.api.browseItemsPublic()
  }
  async changeAgreementVersionStaff(request: AgreementVersionChangeRequest): Promise<AgreementInstance> {
    return this.api.changeAgreementVersionStaff(request)
  }
  async changeItemPriceStaff(request: CatalogPriceChangeRequest): Promise<CatalogItemInstance> {
    return this.api.changeItemPriceStaff(request)
  }
  async onSaleItemInstancesStaff(): Promise<Array<CatalogItemInstanceStaff>> {
    return this.api.onSaleItemInstancesStaff()
  }
  async onSaleItemInstancesMember(): Promise<Array<CatalogItemInstanceMember>> {
    return this.api.onSaleItemInstancesMember()
  }
  async saveCatalogItemStaff(request: SaveCatalogItemRequest): Promise<CatalogItemStaff> {
    return this.api.saveCatalogItemStaff(request)
  }
}
